import { useState, useEffect, useMemo } from 'react'
import { Trophy, Loader2, Plus, Minus, RotateCcw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

/* Match-resultaat invoeren.
   BO1 (best_of=1): één score per team, winner-radio, opslaan als "16-14".
   BO3/BO5/BO7 (best_of=3|5|7): per game een rij met s1/s2. Match-winner =
   eerste team dat (best_of+1)/2 games wint. game_scores → "16-14,12-16,16-10".
   Legacy .score krijgt de series-score in "games-games" ("2-1"). */
export default function MatchResultDialog({ open, onClose, match, onSubmit, loading }) {
  const bestOf = Number(match?.best_of || 1)
  const target = Math.floor(bestOf / 2) + 1   // BO3→2, BO5→3, BO7→4, BO1→1

  const [winnerId, setWinnerId] = useState('')
  const [s1, setS1] = useState('')             // BO1 only
  const [s2, setS2] = useState('')             // BO1 only
  const [games, setGames] = useState([{ a: '', b: '' }])  // BO>1
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!match) return
    setWinnerId(match.winner_id?.toString() || '')
    setError(null)
    if (bestOf === 1) {
      const parts = String(match.score || '').match(/^(\d+)\s*[-:]\s*(\d+)$/)
      setS1(parts ? parts[1] : '')
      setS2(parts ? parts[2] : '')
    } else {
      const parsed = parseGames(match.game_scores)
      setGames(parsed.length ? parsed : [{ a: '', b: '' }])
    }
  }, [match, bestOf])

  // Games-count per team + auto-suggest winner in BO>1.
  const tally = useMemo(() => tallyGames(games), [games])
  useEffect(() => {
    if (bestOf === 1 || !match?.participant1_id || !match?.participant2_id) return
    if (tally.a >= target && tally.a > tally.b) setWinnerId(String(match.participant1_id))
    else if (tally.b >= target && tally.b > tally.a) setWinnerId(String(match.participant2_id))
  }, [tally.a, tally.b, target, bestOf, match?.participant1_id, match?.participant2_id])

  if (!match) return null

  const p1 = { id: match.participant1_id, name: match.participant1_name || 'TBD' }
  const p2 = { id: match.participant2_id, name: match.participant2_name || 'TBD' }

  const suggestWinnerBO1 = (a, b) => {
    const na = parseInt(a || '', 10); const nb = parseInt(b || '', 10)
    if (Number.isNaN(na) || Number.isNaN(nb) || na === nb) return
    setWinnerId(String(na > nb ? p1.id : p2.id))
  }

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!winnerId) { setError('Kies een winnaar'); return }

    if (bestOf === 1) {
      const na = parseInt(s1, 10); const nb = parseInt(s2, 10)
      if (Number.isNaN(na) || Number.isNaN(nb)) { setError('Geef beide scores op'); return }
      if (na < 0 || nb < 0) { setError('Score kan niet negatief zijn'); return }
      try {
        await onSubmit({
          winner_id: parseInt(winnerId, 10),
          score: `${na}-${nb}`,
          status: 'completed',
        })
        onClose()
      } catch (err) {
        setError(err.response?.data?.error || 'Opslaan mislukt')
      }
      return
    }

    // BO>1: valideer minstens één ingevulde game én dat winner de target gehaald heeft.
    const filled = games.filter(g => g.a !== '' && g.b !== '')
    if (!filled.length) { setError('Vul minstens één game in'); return }
    if (filled.some(g => Number.isNaN(parseInt(g.a, 10)) || Number.isNaN(parseInt(g.b, 10)))) {
      setError('Alle games moeten twee getallen hebben'); return
    }
    if (tally.a < target && tally.b < target) {
      setError(`Nog geen winnaar — eerste met ${target} game-winsten wint`); return
    }
    const gs = filled.map(g => `${parseInt(g.a, 10)}-${parseInt(g.b, 10)}`).join(',')
    try {
      await onSubmit({
        winner_id: parseInt(winnerId, 10),
        score: `${tally.a}-${tally.b}`,          // series-score in "games-games"
        game_scores: gs,
        status: 'completed',
      })
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt')
    }
  }

  const handleClose = () => { setError(null); onClose() }

  const teamRow = (p, val, setVal, idx) => {
    const isWinner = winnerId === String(p.id)
    return (
      <div
        onClick={() => p.id && setWinnerId(String(p.id))}
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
          isWinner ? 'border-live/60 bg-live/10' : 'border-border hover:border-acid/50'
        )}
      >
        <input type="radio" name="winner" checked={isWinner} readOnly
               className="h-4 w-4 accent-live shrink-0" disabled={!p.id} />
        <div className="flex-1 min-w-0">
          <div className={cn('font-semibold truncate', isWinner && 'text-live')}>{p.name}</div>
          <div className="text-xs text-muted-foreground">Speler / team {idx}</div>
        </div>
        <Input type="number" min="0" value={val} onChange={e => setVal(e.target.value)}
               placeholder="0"
               className="w-20 text-right tabular-nums text-lg font-bold"
               onClick={e => e.stopPropagation()} />
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-acid" />
            Match-resultaat {bestOf > 1 && <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">BO{bestOf}</span>}
          </DialogTitle>
          <DialogDescription>
            {bestOf === 1
              ? 'Vul de score per team in en kies de winnaar. Klik op de rij om die als winnaar te selecteren.'
              : `Best-of-${bestOf}: eerste met ${target} game-winsten wint. Voeg een rij toe per gespeelde game.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {bestOf === 1 ? (
            <>
              <div className="space-y-2">
                <Label>Score per team</Label>
                {teamRow(p1, s1, (v) => { setS1(v); suggestWinnerBO1(v, s2) }, 1)}
                {teamRow(p2, s2, (v) => { setS2(v); suggestWinnerBO1(s1, v) }, 2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Score wordt bewaard als "{s1 || '?'}-{s2 || '?'}" — zichtbaar in de bracket per team.
              </p>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Games — stand: {tally.a}-{tally.b} <span className="text-muted-foreground">(target {target})</span></Label>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="outline"
                            disabled={games.length >= bestOf}
                            onClick={() => setGames([...games, { a: '', b: '' }])}>
                      <Plus className="h-3 w-3 mr-1" />Game
                    </Button>
                    <Button type="button" size="sm" variant="outline"
                            disabled={games.length <= 1}
                            onClick={() => setGames(games.slice(0, -1))}>
                      <Minus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
                  <div />
                  <div className="text-xs text-muted-foreground truncate">{p1.name}</div>
                  <div />
                  <div className="text-xs text-muted-foreground truncate">{p2.name}</div>
                  {games.map((g, i) => (
                    <GameRow key={i}
                             index={i}
                             game={g}
                             onChange={(g2) => setGames(games.map((x, j) => j === i ? g2 : x))} />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Match-winnaar</Label>
                {teamRow(p1, '', () => {}, 1)}
                {teamRow(p2, '', () => {}, 2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Games worden bewaard als "{games.map(g => `${g.a || '?'}-${g.b || '?'}`).join(',')}".
              </p>
            </>
          )}

          {error && (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          )}

          <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            {/* Reset — alleen zichtbaar bij completed matches. Zet winner=null,
                score=null, status=pending zodat de match opnieuw gespeeld kan.
                Downstream doorschuif wordt NIET auto-teruggerold; admin krijgt
                de waarschuwing in de confirm. */}
            {match.status === 'completed' && (
              <Button type="button" variant="outline"
                      className="text-signal border-signal/40 hover:border-signal"
                      disabled={loading}
                      onClick={async () => {
                        if (!confirm('Match resetten? De uitslag wordt gewist en de match komt terug op "in te spelen". Als de winnaar al is doorgeschoven naar een volgende ronde blijft die daar staan — pas dat handmatig aan indien nodig.')) return
                        try {
                          await onSubmit({ winner_id: null, score: null, game_scores: null, status: 'pending' })
                          onClose()
                        } catch (e) {
                          setError(e.response?.data?.error || 'Reset mislukt')
                        }
                      }}>
                <RotateCcw className="h-4 w-4 mr-1" /> Reset / herspelen
              </Button>
            )}
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading || !winnerId}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {match.status === 'completed' ? 'Resultaat bijwerken' : 'Resultaat opslaan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function GameRow({ index, game, onChange }) {
  return (
    <>
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground text-right pr-1">
        G{index + 1}
      </div>
      <Input type="number" min="0" value={game.a}
             onChange={e => onChange({ ...game, a: e.target.value })}
             className="h-9 text-right tabular-nums" placeholder="0" />
      <div className="text-center text-muted-foreground">—</div>
      <Input type="number" min="0" value={game.b}
             onChange={e => onChange({ ...game, b: e.target.value })}
             className="h-9 text-right tabular-nums" placeholder="0" />
    </>
  )
}

function parseGames(str) {
  if (!str) return []
  return String(str).split(',')
    .map(s => s.trim().match(/^(\d+)\s*[-:]\s*(\d+)$/))
    .filter(Boolean)
    .map(m => ({ a: m[1], b: m[2] }))
}

function tallyGames(games) {
  let a = 0, b = 0
  for (const g of games) {
    const na = parseInt(g.a, 10); const nb = parseInt(g.b, 10)
    if (Number.isNaN(na) || Number.isNaN(nb) || na === nb) continue
    if (na > nb) a++
    else b++
  }
  return { a, b }
}
